import { BrianMark } from "@scribenote/brian-design-system";
import { DisplayFrame } from "@shell/DisplayFrame";

import { ClinicBoardDisplay } from "./ClinicBoardDisplay";
import {
  type Display,
  DISPLAY_KIND_LABEL,
  type DisplayKind,
} from "./displaysClient";
import { useDisplayScreen, useDisplayView } from "./displaysQueries";
import { ExamRoomDisplay } from "./ExamRoomDisplay";
import { TreatmentBoardDisplay } from "./TreatmentBoardDisplay";
import { useDisplayViewSocket } from "./useDisplayViewSocket";

/** Kinds brian has no view for yet. */
const NoViewYet = ({ display }: { display: Display }) => (
  <DisplayFrame tapes={[display.name]}>
    <div
      data-testid="display-home"
      className="m-auto flex flex-col items-center gap-3 text-center"
    >
      <p className="font-display text-[2.5rem] font-medium text-ink">
        {DISPLAY_KIND_LABEL[display.kind]}
      </p>
      <p className="text-[1.1875rem] text-annotation">
        Paired. Nothing on this screen yet.
      </p>
    </div>
  </DisplayFrame>
);

/** The view's kind picks the screen once it loads; the display's until then. */
const ViewHome = ({ display }: { display: Display }) => {
  useDisplayViewSocket();
  const { data: view } = useDisplayView();
  const kind = view?.kind ?? display.kind;

  if (kind === "exam_room")
    return (
      <ExamRoomDisplay
        roomName={view?.kind === "exam_room" ? view.roomName : display.name}
        sheets={view?.kind === "exam_room" ? view.sheets : null}
        requestedView={view?.kind === "exam_room" ? view.requestedView : null}
        question={view?.kind === "exam_room" ? view.question : null}
      />
    );
  if (kind === "clinic_board")
    return (
      <ClinicBoardDisplay
        displayId={display.id}
        name={display.name}
        visits={view?.kind === "clinic_board" ? view.visits : null}
        roster={view?.kind === "clinic_board" ? view.roster : null}
        rotation={display.rotation}
      />
    );
  if (kind === "treatment_board")
    return (
      <TreatmentBoardDisplay
        name={display.name}
        patients={view?.kind === "treatment_board" ? view.patients : null}
      />
    );
  return <NoViewYet display={display} />;
};

const HAS_VIEW: ReadonlySet<DisplayKind> = new Set([
  "exam_room",
  "clinic_board",
  "treatment_board",
]);

export const DisplayHome = () => {
  const { data: display } = useDisplayScreen(true);
  if (!display) return null;

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-wall font-body text-ink-body">
      {HAS_VIEW.has(display.kind) ? (
        <ViewHome display={display} />
      ) : (
        <NoViewYet display={display} />
      )}
      <BrianMark size={40} className="fixed right-6 bottom-6" />
    </div>
  );
};
